import React, { useState } from 'react';
import { 
  TrendingUp, Shield, Globe, FileCheck, Package, 
  DollarSign, Truck, Building2, CheckCircle, ArrowRight,
  Percent, Scale, Handshake, Landmark, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import QuickContactModal from '../components/QuickContactModal';

const IndiaUAETrade = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const cepaHighlights = [
    {
      icon: Percent,
      title: "Zero/Reduced Tariffs",
      value: "80%+",
      description: "Over 80% of products traded between UAE and India enjoy zero or reduced tariffs under CEPA"
    },
    {
      icon: TrendingUp,
      title: "Trade Growth",
      value: "$100B+",
      description: "Bilateral trade exceeded $100 billion in FY 2024-25"
    },
    {
      icon: Globe,
      title: "Export Growth",
      value: "20.5%",
      description: "Non-oil bilateral trade grew by 20.5% after CEPA implementation"
    },
    {
      icon: Building2,
      title: "Service Sectors",
      value: "100+",
      description: "Expanded access across 11 sectors and 100+ sub-sectors"
    }
  ];

  const tradeBenefits = [
    {
      icon: Shield,
      title: "CEPA Benefits",
      subtitle: "India-UAE Comprehensive Economic Partnership Agreement",
      benefits: [
        "Zero tariffs on pharmaceuticals, gems & jewellery exports",
        "Reduced duties on engineering goods and textiles",
        "Preferential market access for IT-enabled services",
        "Protection from anti-dumping investigations",
        "Faster customs clearance procedures",
        "Access to UAE government procurement"
      ],
      color: "from-blue-500 to-blue-700"
    },
    {
      icon: DollarSign,
      title: "US Tariff Savings",
      subtitle: "Strategic Re-Export Gateway",
      benefits: [
        "Bypass 25-50% US tariff hikes through UAE routing",
        "UAE as transshipment hub to access global markets",
        "Leverage UAE's FTA network with 40+ countries",
        "Reduce effective duties through CEPA preferences",
        "Supply chain resilience and diversification",
        "Strategic positioning for US-India trade deals"
      ],
      color: "from-green-500 to-green-700"
    },
    {
      icon: Package,
      title: "Export Advantages",
      subtitle: "Key Sectors with Maximum Benefits",
      benefits: [
        "Pharmaceuticals - Zero duty access to UAE market",
        "Gems & Jewellery - Immediate tariff elimination",
        "Textiles & Apparel - Significant duty reductions",
        "Engineering Goods - Preferential market access",
        "IT & Digital Services - Expanded opportunities",
        "Food & Agriculture - Growing export potential"
      ],
      color: "from-purple-500 to-purple-700"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Identify Your HS Codes",
      description: "Determine the Harmonized System codes for your products and check current MFN tariff rates"
    },
    {
      step: 2,
      title: "Check CEPA Rates",
      description: "Review CEPA tariff schedules for preferential rates and phase-in timelines for your products"
    },
    {
      step: 3,
      title: "Meet Origin Requirements",
      description: "Ensure your goods meet CEPA rules of origin (materials/processing thresholds)"
    },
    {
      step: 4,
      title: "Obtain Certificates",
      description: "Prepare Certificate of Origin and required exporter/importer documentation"
    },
    {
      step: 5,
      title: "Claim Preferences",
      description: "Submit documentation at customs to claim preferential tariff treatment"
    },
    {
      step: 6,
      title: "Realize Savings",
      description: "Enjoy reduced duties: (MFN tariff – CEPA rate) × value = your savings"
    }
  ];

  const sectors = [
    { name: "Pharmaceuticals", saving: "Up to 100%", status: "Zero Duty" },
    { name: "Gems & Jewellery", saving: "Up to 100%", status: "Zero Duty" },
    { name: "Textiles", saving: "Up to 90%", status: "Reduced" },
    { name: "Engineering Goods", saving: "Up to 85%", status: "Reduced" },
    { name: "Chemicals", saving: "Up to 80%", status: "Reduced" },
    { name: "Food Products", saving: "Up to 75%", status: "Reduced" },
    { name: "Plastics", saving: "Up to 70%", status: "Reduced" },
    { name: "IT Services", saving: "Expanded", status: "Market Access" }
  ];

  const faqs = [
    {
      question: "What is CEPA and when did it come into effect?",
      answer: "CEPA (Comprehensive Economic Partnership Agreement) between India and UAE came into effect on May 1, 2022. It's a comprehensive trade agreement covering goods, services, investment, and digital trade, making it easier and more profitable for businesses to trade between the two countries."
    },
    {
      question: "How can my business benefit from CEPA tariff savings?",
      answer: "To benefit from CEPA, your products must meet the Rules of Origin requirements. You'll need to obtain a Certificate of Origin, ensure proper documentation, and claim the preferential tariff rate at customs. Savings can range from 70% to 100% depending on your product category."
    },
    {
      question: "Can I use UAE as a gateway to avoid US tariffs?",
      answer: "Yes, UAE serves as a strategic re-export hub. By manufacturing or significantly processing goods in UAE (meeting value-addition requirements), you can leverage UAE's trade agreements and potentially avoid direct tariffs. However, you must comply with rules of origin and circumvention regulations."
    },
    {
      question: "What documents are required to claim CEPA benefits?",
      answer: "Key documents include: Certificate of Origin (issued by authorized bodies), commercial invoice, packing list, bill of lading/airway bill, and sometimes additional proof of manufacturing or processing to meet origin criteria."
    },
    {
      question: "How do I set up a company in UAE to leverage these benefits?",
      answer: "Firmzy can help you establish a UAE entity (Free Zone, Mainland, or Offshore) that's optimized for trade benefits. We handle licensing, documentation, and compliance so you can focus on growing your business."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1929] via-[#1a1033] to-[#0A1929] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-orange-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-500 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center gap-4 mb-6">
              <img src="https://flagcdn.com/w80/in.png" alt="India" className="h-12 rounded shadow-lg" />
              <Handshake size={48} className="text-[#DC143C]" />
              <img src="https://flagcdn.com/w80/ae.png" alt="UAE" className="h-12 rounded shadow-lg" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              India-UAE <span className="text-[#DC143C]">Trade Benefits</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Unlock massive tariff savings with CEPA, leverage UAE as your gateway to global markets, 
              and bypass US tariff barriers through strategic business setup.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white px-8 py-6 text-lg hover:shadow-xl transition-all"
              >
                Calculate Your Savings
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Benefits
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CEPA Highlights */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {cepaHighlights.map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl bg-gray-50 hover:shadow-lg transition-all">
                <item.icon className="mx-auto mb-3 text-[#DC143C]" size={32} />
                <p className="text-3xl md:text-4xl font-bold text-gray-800 mb-1">{item.value}</p>
                <p className="text-sm font-semibold text-[#6B1B4D] mb-2">{item.title}</p>
                <p className="text-xs text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trade Benefits Section */}
      <section id="benefits" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Key Trade <span className="text-[#DC143C]">Program Benefits</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Maximize your competitive advantage with these strategic trade benefits available to Indian businesses operating through UAE
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {tradeBenefits.map((benefit, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
                <div className={`bg-gradient-to-r ${benefit.color} p-6 text-white`}>
                  <benefit.icon size={40} className="mb-3" />
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                  <p className="text-sm opacity-90">{benefit.subtitle}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {benefit.benefits.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sector-wise Savings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Sector-wise <span className="text-[#DC143C]">Tariff Savings</span>
            </h2>
            <p className="text-gray-600">Indicative savings under CEPA by industry sector</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sectors.map((sector, idx) => (
                <div key={idx} className="bg-gradient-to-br from-gray-50 to-white p-5 rounded-xl border border-gray-100 hover:border-[#DC143C]/30 hover:shadow-md transition-all">
                  <h4 className="font-semibold text-gray-800 text-sm mb-2">{sector.name}</h4>
                  <p className="text-2xl font-bold text-[#DC143C]">{sector.saving}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    sector.status === 'Zero Duty' ? 'bg-green-100 text-green-700' :
                    sector.status === 'Market Access' ? 'bg-blue-100 text-blue-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {sector.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-br from-[#6B1B4D] to-[#2D1B3D] text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How to Claim <span className="text-[#DC143C]">CEPA Benefits</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Follow these steps to maximize your tariff savings under the India-UAE trade agreement
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {howItWorks.map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-all">
                  <div className="w-10 h-10 bg-[#DC143C] rounded-full flex items-center justify-center text-white font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* US Tariff Strategy Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  UAE: Your Gateway to <span className="text-[#DC143C]">US Markets</span>
                </h2>
                <p className="text-gray-600 mb-6">
                  With US tariffs on Indian goods rising from 25% to 50% in some categories, 
                  smart businesses are using UAE as a strategic hub to maintain competitiveness.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Scale size={18} className="text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Value Addition in UAE</h4>
                      <p className="text-sm text-gray-600">Meet origin requirements through UAE processing to qualify for preferential treatment</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Truck size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Strategic Re-exports</h4>
                      <p className="text-sm text-gray-600">Leverage UAE's position as a global logistics hub with 40+ FTA partners</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Landmark size={18} className="text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Supply Chain Resilience</h4>
                      <p className="text-sm text-gray-600">Diversify risk and stabilize costs through UAE's world-class infrastructure</p>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="mt-8 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white px-6 py-3"
                >
                  Get Expert Consultation
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Potential Savings Calculator</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-1">Current US Tariff on Indian Goods</p>
                    <p className="text-2xl font-bold text-red-500">25% - 50%</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-1">Effective Rate via UAE Route</p>
                    <p className="text-2xl font-bold text-green-500">0% - 15%</p>
                  </div>
                  <div className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] rounded-xl p-4 text-white">
                    <p className="text-sm opacity-90 mb-1">Your Potential Savings</p>
                    <p className="text-3xl font-bold">Up to 50%</p>
                    <p className="text-xs opacity-80 mt-1">*Subject to product category and origin compliance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Frequently Asked <span className="text-[#DC143C]">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-100 transition-all"
                >
                  <span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
                  {expandedFaq === idx ? (
                    <ChevronUp size={20} className="text-[#DC143C] flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Maximize Your Trade Benefits?
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Our experts will analyze your business and identify the best UAE setup structure 
            to maximize CEPA benefits and tariff savings.
          </p>
          <Button 
            onClick={() => setIsModalOpen(true)}
            className="bg-white text-[#6B1B4D] hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
          >
            Get Free Consultation
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <QuickContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        defaultService="Business Consultancy"
      />
    </div>
  );
};

export default IndiaUAETrade;
